/**
 * Flexv wordmark — diambil dari FLexv Portal
 * (frontend/app/components/ui/FlexvLogo.tsx).
 *
 * Semua path pakai `currentColor`, jadi warnanya otomatis mengikuti tema
 * aktif (cukup kasih kelas warna seperti `app-text-primary`).
 */

/** Glyph "F" saja — bounds-nya x[-0.01 .. 12.04] y[4.97 .. 20.98]. */
const F_MARK =
    "M12.0356 13.376C12.0356 14.415 11.187 15.2568 10.1417 15.2568H3.81442C3.78348 15.6711 3.6995 16.1818 3.53154 16.7342C3.52491 16.7583 3.51828 16.7868 3.50944 16.811C3.49397 16.8658 3.47629 16.9206 3.4564 16.9754C3.45198 16.9819 3.44977 16.9885 3.44756 16.9973C3.41441 17.1003 3.37463 17.2033 3.33485 17.3064C3.31496 17.3634 3.29286 17.4182 3.26634 17.4752C3.17352 17.7009 3.06523 17.9267 2.94368 18.1547C2.90832 18.2226 2.87075 18.284 2.83318 18.3498C2.81992 18.3739 2.80445 18.398 2.79119 18.4221C2.19891 19.3866 1.37679 20.1955 0.399965 20.7742C0.377865 20.7874 0.353555 20.8005 0.327035 20.8137C0.304935 20.8268 0.282835 20.8378 0.258525 20.8487C0.249685 20.8531 0.243055 20.8575 0.234215 20.8619C0.161285 20.9014 0.0883546 20.9408 0.0110044 20.9759V9.61879C-0.00446563 9.40835 -0.00446563 9.16722 0.0132144 8.90856C0.0154244 8.84718 0.0220544 8.7858 0.0286845 8.72004C0.187805 7.10887 1.07402 4.96722 3.82768 4.96722H11.8964C12.2986 6.26493 12.071 8.88225 7.97145 8.96775C7.85211 8.96994 7.73497 8.97432 7.62226 8.9809C4.22549 9.14311 3.6111 10.5745 3.75696 11.493H10.1439C10.166 11.493 10.1903 11.493 10.2168 11.4952C11.2312 11.5325 12.0401 12.3567 12.0401 13.3716L12.0356 13.376Z";

/** Wordmark utuh: F + L + E + X + V. */
const WORDMARK = [
    F_MARK,
    "M17.7466 17.3853V4.96941H13.183V20.9759H25.4463V17.3853H17.7466Z",
    "M38.7618 17.4773C36.47 18.8035 34.0346 20.0793 31.8268 20.9759H39.5442V17.4773H38.7618ZM31.1086 17.4773V14.5509H38.2778V11.1664H31.1086V8.46795H39.2458V4.96941H26.5891V17.9399C27.2676 15.6426 28.651 13.2072 28.7196 13.0888C28.6643 13.2664 26.9692 18.7794 30.2864 18.7071C31.1638 18.6874 32.7771 18.1963 34.6866 17.4773H31.1086Z",
    "M51.799 12.6986L57.4234 4.96941H52.4907L49.3083 9.58809L46.0817 4.96941H40.9412L46.1237 12.2602L49.1403 10.9932C48.8619 11.2409 48.5547 11.4952 48.2143 11.7517C48.0198 11.9007 47.8254 12.0366 47.6375 12.1638L46.3623 13.159L40.687 20.9759H45.896L49.1934 16.013L52.5349 20.9759H57.7925L51.799 12.6986Z",
    "M74.2595 13.5755C74.2595 13.5755 77.8198 13.0954 76.7944 14.415C75.7712 15.7368 71.9302 18.4769 71.9302 18.4769L74.2573 13.5733L74.2595 13.5755Z",
    "M77.4883 3.63006C78.4989 3.63006 79.3182 2.81745 79.3182 1.81503C79.3182 0.812617 78.4989 0 77.4883 0C76.4777 0 75.6584 0.812617 75.6584 1.81503C75.6584 2.81745 76.4777 3.63006 77.4883 3.63006Z",
    "M73.7579 7.25793H79.3183L77.4022 10.386H73.7579V7.25793Z",
    "M77.2674 4.96942L70.3412 21H65.8394L58.9354 4.96942H63.8769L68.2638 15.4124L72.7435 4.96942H77.2696H77.2674Z",
];

export function FlexvLogo({
    className = "",
    compact = false,
    title,
}: {
    className?: string;
    /** Hanya glyph "F" — dipakai saat sidebar collapse (rail). */
    compact?: boolean;
    /** A11y: label yang dibaca screen reader. */
    title?: string;
}) {
    if (compact) {
        return (
            <svg
                viewBox="-0.1 4.85 12.25 16.25"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={className}
                role={title ? "img" : undefined}
                aria-hidden={title ? undefined : true}
                aria-label={title}
            >
                {title ? <title>{title}</title> : null}
                <path d={F_MARK} fill="currentColor" />
            </svg>
        );
    }

    return (
        <svg
            viewBox="0 0 80 21"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={className}
            role={title ? "img" : undefined}
            aria-hidden={title ? undefined : true}
            aria-label={title}
        >
            {title ? <title>{title}</title> : null}
            <g clipPath="url(#flexv-logo-clip)">
                {WORDMARK.map((d) => (
                    <path key={d.slice(0, 24)} d={d} fill="currentColor" />
                ))}
            </g>
            <defs>
                <clipPath id="flexv-logo-clip">
                    <rect width="80" height="21" fill="white" />
                </clipPath>
            </defs>
        </svg>
    );
}
